<template>
  <div class="booking-shell" :style="bookingThemeStyle">
    <v-container class="booking-container">
      <div v-if="isLoading" class="booking-loading">
        <v-progress-circular indeterminate color="primary" size="54"></v-progress-circular>
        <strong>Angebot wird geladen</strong>
        <span>Einen kleinen Moment bitte.</span>
      </div>

      <main v-else-if="reservationType" class="booking-layout">
        <header class="booking-header text-center">
          <span class="booking-eyebrow">ANGEBOT RESERVIEREN</span>
          <span class="booking-offer-icon"><v-icon>mdi-spa-outline</v-icon></span>
          <h1 class="booking-title">{{ reservationType.name }}</h1>
          <p v-if="reservationType.description" class="booking-description">{{ reservationType.description }}</p>
        </header>

        <div class="booking-progress" aria-label="Buchungsschritte">
          <button
            type="button"
            class="progress-step"
            :class="{ 'progress-step--active': step === 1, 'progress-step--complete': step > 1 }"
            :aria-current="step === 1 ? 'step' : null"
            @click="moveToStep(1)">
            <span class="progress-step-number"><v-icon v-if="step > 1" small>mdi-check</v-icon><span v-else>1</span></span>
            <span class="progress-step-copy">
              <strong>Datum</strong>
              <small>{{ selectedDate ? formatShortDate(selectedDate) : "Auswählen" }}</small>
            </span>
          </button>
          <span class="progress-connector" :class="{ 'progress-connector--active': step > 1 }"></span>
          <button
            type="button"
            class="progress-step"
            :class="{ 'progress-step--active': step === 2 }"
            :aria-current="step === 2 ? 'step' : null"
            :disabled="!selectedDate"
            @click="moveToStep(2)">
            <span class="progress-step-number">2</span>
            <span class="progress-step-copy">
              <strong>Uhrzeit</strong>
              <small>{{ selectedTimeslot ? formatTime(selectedTimeslot) + " Uhr" : "Danach" }}</small>
            </span>
          </button>
        </div>

        <v-card class="booking-panel" flat>
          <v-window v-model="step" touchless>
            <v-window-item :value="1">
              <section class="step-wrapper">
                <div class="step-heading">
                  <span class="step-icon"><v-icon>mdi-calendar-month-outline</v-icon></span>
                  <div>
                    <span class="step-kicker">SCHRITT 1 VON 2</span>
                    <h2>Wann möchtest du kommen?</h2>
                    <p>Wähle zuerst den gewünschten Tag aus.</p>
                  </div>
                </div>

                <div class="date-picker-frame">
                  <v-date-picker
                    v-model="selectedDate"
                    full-width
                    no-title
                    :min="today"
                    :max="maxDateSelect"
                    :first-day-of-week="1"
                    locale="de-CH"
                    dark
                    color="primary"
                    class="date-picker-custom"></v-date-picker>
                </div>

                <div class="step-footer">
                  <div class="selected-date-preview">
                    <v-icon>mdi-calendar-check-outline</v-icon>
                    <div>
                      <small>Ausgewähltes Datum</small>
                      <strong>{{ formatDate(selectedDate) }}</strong>
                    </div>
                  </div>
                  <v-btn color="primary" large depressed class="step-primary-action text-none" :disabled="!selectedDate" @click="moveToStep(2)">
                    Freie Zeiten anzeigen
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </section>
            </v-window-item>

            <v-window-item :value="2">
              <section class="step-wrapper">
                <div class="selected-date-bar">
                  <span>
                    <v-icon>mdi-calendar-outline</v-icon>
                    <span>
                      <small>Gewählter Tag</small>
                      <strong>{{ formatDate(selectedDate) }}</strong>
                    </span>
                  </span>
                  <v-btn text small class="change-date-btn text-none" @click="moveToStep(1)">
                    <v-icon left small>mdi-pencil-outline</v-icon>
                    Ändern
                  </v-btn>
                </div>

                <div class="step-heading step-heading--times">
                  <span class="step-icon"><v-icon>mdi-clock-outline</v-icon></span>
                  <div>
                    <span class="step-kicker">SCHRITT 2 VON 2</span>
                    <h2>Welche Uhrzeit passt?</h2>
                    <p v-if="availableTimeslotCount">{{ availableTimeslotCount }} freie Zeiten an diesem Tag.</p>
                    <p v-else>Wähle eine freie Uhrzeit aus.</p>
                  </div>
                </div>

                <div v-if="showGroupFitnessAlert" class="booking-info-banner">
                  <v-icon>mdi-information-outline</v-icon>
                  <span>Reservierungen sind nur während der betreuten Öffnungszeiten möglich.</span>
                </div>

                <div
                  v-if="userBookingInfo && userBookingInfo.limit < 999"
                  class="booking-limit-info"
                  :class="{ 'booking-limit-info--blocked': userBookingInfo.remaining === 0 }">
                  <v-icon>{{ userBookingInfo.remaining > 0 ? "mdi-ticket-confirmation-outline" : "mdi-alert-outline" }}</v-icon>
                  <span v-if="userBookingInfo.remaining > 0">
                    Noch <strong>{{ userBookingInfo.remaining }}</strong> von {{ userBookingInfo.limit }} Buchungen {{ prep[userBookingInfo.unit] }}
                    {{ userBookingInfo.unit }} frei.
                  </span>
                  <span v-else>Dein Buchungslimit für diesen Zeitraum ist erreicht.</span>
                </div>

                <div v-if="isFetchingSlots" class="slots-loading">
                  <v-progress-circular indeterminate color="primary" size="42"></v-progress-circular>
                  <span>Freie Zeiten werden geladen.</span>
                </div>

                <div v-else-if="filteredTimeslots.length && (!userBookingInfo || userBookingInfo.remaining > 0)" class="timeslot-grid">
                  <v-btn
                    v-for="slot in filteredTimeslots"
                    :key="slot.time"
                    :disabled="slot.isBooked"
                    :aria-label="'Uhrzeit ' + formatTime(slot.time) + (slot.isBooked ? ' belegt' : ' auswählen')"
                    class="timeslot-btn text-none"
                    depressed
                    @click="selectSlotAndProceed(slot.time)">
                    <span>{{ formatTime(slot.time) }}</span>
                    <small v-if="slot.isBooked">belegt</small>
                    <small v-else>frei</small>
                  </v-btn>
                </div>

                <div v-else-if="userBookingInfo && userBookingInfo.remaining === 0" class="no-slots-container">
                  <span class="no-slots-icon"><v-icon>mdi-calendar-lock-outline</v-icon></span>
                  <h3>Aktuell keine weitere Buchung möglich</h3>
                  <p>Du hast dein Buchungslimit für diesen Zeitraum erreicht.</p>
                  <v-btn outlined color="primary" class="text-none" @click="moveToStep(1)">Anderen Tag ansehen</v-btn>
                </div>

                <div v-else class="no-slots-container">
                  <span class="no-slots-icon"><v-icon>mdi-calendar-search-outline</v-icon></span>
                  <h3>An diesem Tag ist nichts frei</h3>
                  <p>Wähle einen anderen Tag und versuche es erneut.</p>
                  <v-btn outlined color="primary" class="text-none" @click="moveToStep(1)">Anderes Datum wählen</v-btn>
                </div>
              </section>
            </v-window-item>
          </v-window>
        </v-card>
      </main>

      <div v-else class="booking-load-error">
        <span><v-icon>mdi-calendar-alert-outline</v-icon></span>
        <h1>Angebot nicht verfügbar</h1>
        <p>Dieses Reservierungsangebot konnte nicht geladen werden.</p>
        <v-btn color="primary" depressed class="text-none" @click="$router.go(-1)">Zurück zur Übersicht</v-btn>
      </div>
    </v-container>

    <v-dialog v-model="bookingDialog" max-width="500px" persistent content-class="booking-dialog">
      <v-card dark class="confirm-dialog-card" :style="bookingThemeStyle">
        <div class="confirm-dialog-content">
          <span class="confirm-icon-wrap"><v-icon>mdi-calendar-check-outline</v-icon></span>
          <span class="dialog-eyebrow">FAST GESCHAFFT</span>
          <h2 class="confirm-title">Reservierung bestätigen</h2>
          <p class="confirm-text">Bitte prüfe deine Auswahl noch einmal.</p>

          <div class="confirmation-summary">
            <div>
              <span><v-icon small>mdi-spa-outline</v-icon> Angebot</span>
              <strong>{{ reservationType ? reservationType.name : "" }}</strong>
            </div>
            <div>
              <span><v-icon small>mdi-calendar-outline</v-icon> Datum</span>
              <strong>{{ formatDate(selectedDate) }}</strong>
            </div>
            <div>
              <span><v-icon small>mdi-clock-outline</v-icon> Uhrzeit</span>
              <strong>{{ selectedTimeRange }}</strong>
            </div>
            <div v-if="selectedStudio">
              <span><v-icon small>mdi-map-marker-outline</v-icon> Studio</span>
              <strong>{{ selectedStudio.name || selectedStudio.code }}</strong>
            </div>
          </div>
        </div>
        <v-card-actions class="confirm-actions">
          <v-btn text large class="text-none" :disabled="isBooking" @click="bookingDialog = false">Nochmals ändern</v-btn>
          <v-btn color="primary" large depressed class="confirm-btn text-none" :loading="isBooking" @click="bookTimeslot">
            Verbindlich reservieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="errorDialog" max-width="460px" persistent content-class="booking-dialog">
      <v-card dark class="confirm-dialog-card" :style="bookingThemeStyle">
        <div class="confirm-dialog-content">
          <span
            class="confirm-icon-wrap"
            :class="{ 'confirm-icon-wrap--error': !isDefectiveError && !isInfoError, 'confirm-icon-wrap--info': isDefectiveError || isInfoError }">
            <v-icon>
              {{ isDefectiveError || isInfoError ? "mdi-information-outline" : "mdi-alert-circle-outline" }}
            </v-icon>
          </span>
          <h2 class="confirm-title">
            {{ isDefectiveError || isInfoError ? "Wichtige Information" : "Buchung fehlgeschlagen" }}
          </h2>
          <p class="confirm-text" v-html="errorMessage"></p>
        </div>
        <v-card-actions class="confirm-actions confirm-actions--single">
          <v-btn
            :color="isDefectiveError || isInfoError ? 'primary' : 'error'"
            large
            depressed
            class="confirm-btn text-none"
            @click="handleErrorDialogClose">
            Verstanden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import moment from "moment"
import userService from "@/services/userService"
import { mapGetters, mapActions, mapState } from "vuex"

moment.locale("de")

export default {
  data() {
    return {
      step: 1,
      userService,
      prep: {
        Tag: "an diesem",
        Woche: "in dieser",
        Monat: "in diesem",
      },
      today: new Date().toISOString().substr(0, 10),
      selectedDate: new Date().toISOString().substr(0, 10),
      maxDateSelect: null,
      selectedTimeslot: null,
      timeslots: [],
      bookingDialog: false,
      isBooking: false,
      studioId: null,
      reservationTypeId: this.$route.params.reservationTypeId,
      reservationType: null,
      isLoading: true,
      isFetchingSlots: false,
      userBookingInfo: null, // NEU: Speichert die Info zu den Limits des Nutzers
      errorDialog: false, // NEU: Steuert den Fehlerdialog
      isDefectiveError: false, // NEU: Status für Defekt-Meldung
      isInfoError: false,
      errorMessage: "",
    }
  },
  watch: {
    selectedDate() {
      this.selectedTimeslot = null
      this.fetchTimeslots()
    },
  },
  async created() {
    this.studioId = this.selectedStudio?._id || null
    await this.fetchReservationTypeDetails()
    await this.fetchTimeslots()
    this.isLoading = false
  },
  computed: {
    ...mapState("studioStore", ["selectedStudio"]),
    ...mapGetters("userStore", [
      "activeContractDetails", // Zugriff auf den Getter courseRecommendations
    ]),
    bookingPrimaryColor() {
      const theme = this.$vuetify && this.$vuetify.theme
      const themes = theme && theme.themes
      const currentTheme = themes && (theme.dark ? themes.dark : themes.light)
      const primary = (currentTheme && currentTheme.primary) || (themes && themes.light && themes.light.primary)
      return typeof primary === "string" && primary.trim() ? primary.trim() : "#009640"
    },
    bookingPrimaryRgb() {
      const value = this.bookingPrimaryColor.replace("#", "")
      const normalized =
        value.length === 3
          ? value
              .split("")
              .map((part) => part + part)
              .join("")
          : value.slice(0, 6)
      if (!/^[0-9a-f]{6}$/i.test(normalized)) return "0, 150, 64"
      return [0, 2, 4].map((index) => parseInt(normalized.slice(index, index + 2), 16)).join(", ")
    },
    bookingThemeStyle() {
      return {
        "--booking-primary": this.bookingPrimaryColor,
        "--booking-primary-rgb": this.bookingPrimaryRgb,
        "--v-primary": this.bookingPrimaryColor,
      }
    },
    availableTimeslotCount() {
      return this.filteredTimeslots.filter((slot) => !slot.isBooked).length
    },
    selectedTimeRange() {
      if (!this.selectedTimeslot || !this.reservationType) return ""
      const start = moment.utc(this.selectedTimeslot)
      const end = start.clone().add(this.reservationType.timeslotDuration || 0, "minutes")
      return `${start.format("HH:mm")} – ${end.format("HH:mm")} Uhr`
    },
    showGroupFitnessAlert() {
      const userSubscriptionTitle = (this.activeContractDetails?.title || "").toLowerCase()
      return userSubscriptionTitle.includes("group fitness")
    },
    filteredTimeslots() {
      const isToday = this.$moment(this.selectedDate).isSame(this.$moment(), "day")

      if (!isToday || !this.timeslots) {
        return this.timeslots
      }

      // 1. Nimm die aktuelle lokale Zeit (z.B. 08:30 CEST)
      const localNow = this.$moment()

      // 2. Erzeuge einen UTC-Zeitstempel, der die gleichen Zahlenwerte hat (wird zu 08:30 UTC)
      const now = this.$moment.utc(localNow.format("YYYY-MM-DD HH:mm:ss"))

      // NEU: Erzeuge den Vergleichszeitpunkt, indem 5 Minuten von der aktuellen Zeit abgezogen werden.
      // .clone() wird verwendet, um das ursprüngliche 'now'-Objekt nicht zu verändern.
      const comparisonTime = now.clone().subtract(5, "minutes")

      // Der Vergleich wird jetzt gegen 'comparisonTime' durchgeführt.
      return this.timeslots.filter((slot) => this.$moment.utc(slot.time).isAfter(comparisonTime))
    },
  },
  methods: {
    /**
     * NEU: Prüft auf den Konflikt mit dem Tag der offenen Tür in Frauenfeld.
     * @param {string} slotTime - Die ISO UTC-Zeit des ausgewählten Slots
     * @returns {boolean} - true, wenn ein Konflikt besteht, sonst false
     */
    checkOpenDayConflict(slotTime) {
      // Bedingung 1: Studio-Code prüfen
      const studioCode = this.selectedStudio?.code
      if (studioCode !== "frauenfeld") {
        return false
      }

      // Bedingung 2: Reservierungstyp prüfen (lowercase)
      const typeName = this.reservationType?.name.toLowerCase()
      if (!typeName || (!typeName.includes("pelvi") && !typeName.includes("hydro"))) {
        return false
      }

      // Bedingung 3: Datum prüfen
      const isBlockedDate = this.selectedDate === "2025-11-01" || this.selectedDate === "2025-11-02"
      if (!isBlockedDate) {
        return false
      }

      // Bedingung 4: Uhrzeit prüfen
      // Wir nehmen an, dass slotTime ein UTC-String ist, der die lokale Zeit repräsentiert
      // (basierend auf deiner formatTime-Logik)
      const slotHour = this.$moment.utc(slotTime).hour()

      // Blockiert ist 9:00 Uhr bis 16:59 Uhr.
      // 17:00 Uhr ist wieder frei ("ab 17 Uhr").
      const isBlockedTime = slotHour >= 9 && slotHour < 17
      if (!isBlockedTime) {
        return false
      }

      // Alle Bedingungen sind erfüllt -> Es gibt einen Konflikt
      return true
    },
    handleErrorDialogClose() {
      this.errorDialog = false

      // Wichtig: Nur bei 'isDefectiveError' wegnavigieren
      if (this.isDefectiveError) {
        this.$router.push({ name: "ReservationTypeSelection" })
      }

      // Beide Info-Flags zurücksetzen
      this.isDefectiveError = false
      this.isInfoError = false
    },
    moveToStep(stepNumber) {
      this.step = stepNumber
    },
    selectSlotAndProceed(slot) {
      // Prüfe zuerst auf den Konflikt
      if (this.checkOpenDayConflict(slot)) {
        // KONFLIKT GEFUNDEN!
        // 1. Setze die Marketing-Fehlermeldung
        this.errorMessage = `Am 1. & 2. November findet unser Tag der offenen Tür statt!<br><br>Buchungen für ${this.reservationType.name} sind an diesen Tagen nur bis 09:00 Uhr und wieder ab 17:00 Uhr möglich.`

        // 2. Setze unseren neuen Info-Status
        this.isInfoError = true
        this.isDefectiveError = false // Sicherstellen, dass der andere Status aus ist

        // 3. Zeige den Fehlerdialog
        this.errorDialog = true
      } else {
        // KEIN KONFLIKT: Normaler Ablauf
        this.selectedTimeslot = slot
        this.bookingDialog = true
      }
    },
    async fetchReservationTypeDetails() {
      try {
        const response = await api.reservationTool.getReservationTypeDetails(this.reservationTypeId)
        this.reservationType = response.data
        this.maxDateSelect = this.$moment().add(14, "days").toISOString().substr(0, 10)
        if (
          this.reservationType.advanceSignupLimit &&
          !isNaN(this.reservationType.advanceSignupLimit) &&
          this.reservationType.advanceSignupLimit !== 0
        ) {
          this.maxDateSelect = this.$moment().add(this.reservationType.advanceSignupLimit, "days").toISOString().substr(0, 10)
        }
        if (this.userService.hasRight("reservations_fetch")) this.maxDateSelect = null
      } catch (error) {
        console.error("Fehler beim Laden der Reservierungsdetails:", error)
        // Optional: User-Feedback
      }
    },
    async fetchTimeslots() {
      if (!this.reservationTypeId || !this.selectedDate) return
      this.isDefectiveError = false // Zurücksetzen vor jeder Abfrage
      this.isFetchingSlots = true
      this.timeslots = []
      this.userBookingInfo = null // Zurücksetzen bei jeder neuen Abfrage
      try {
        const response = await api.reservationTool.getTimeslots(this.reservationTypeId, this.selectedDate)
        const responseData = response.data || {}
        this.timeslots = Array.isArray(responseData.timeslots) ? responseData.timeslots : []
        this.userBookingInfo = responseData.userBookingInfo || null
      } catch (error) {
        if (error.response && error.response.status === 409 && error.response.data.isDefective) {
          this.errorMessage = error.response.data.message
          this.isDefectiveError = true // Status setzen
          this.errorDialog = true
          this.timeslots = []
        } else {
          console.error("Fehler beim Laden der Timeslots:", error)
        }
      } finally {
        this.isFetchingSlots = false
      }
    },
    async bookTimeslot() {
      if (!this.selectedTimeslot) return
      this.isBooking = true

      const bookingData = {
        title: this.reservationType.name,
        description: `Buchung für ${this.userService.getFullName()}`,
        studio: this.studioId,
        user: this.userService.getUser()._id,
        startTime: this.selectedTimeslot,
        endTime: moment.utc(this.selectedTimeslot).add(this.reservationType.timeslotDuration, "minutes").toISOString(),
        isBooked: true,
        reservationType: this.reservationTypeId,
      }

      try {
        await api.reservationTool.createReservation(bookingData)
        this.bookingDialog = false // Erfolgreich -> Weiterleitung
        this.$toast.success("Buchung erfolgreich!")
        this.$router.push("/reservation") // Ziel anpassen
      } catch (error) {
        console.error("Fehler bei der Buchung:", error)
        this.bookingDialog = false // Bestätigungsdialog schliessen

        // NEU: Spezifische Fehlermeldung vom Backend abfangen
        if (error.response && error.response.status === 409) {
          const responseData = error.response.data
          this.errorMessage =
            (responseData && responseData.message) ||
            (typeof responseData === "string" ? responseData : "Diese Zeit ist leider nicht mehr verfügbar.")
          this.errorDialog = true
        } else {
          // Allgemeine Fehlermeldung
          this.errorMessage = "Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es später erneut."
          this.errorDialog = true
        }
      } finally {
        this.isBooking = false
      }
    },
    formatDate(date) {
      // KORREKTUR: Behandle das Datum als UTC, um Verschiebungen zu vermeiden
      return moment.utc(date).format("dddd, DD. MMMM YYYY")
    },

    formatShortDate(date) {
      return moment.utc(date).format("DD. MMM")
    },

    formatTime(time) {
      // KORREKTUR: Behandle den Zeitstempel als UTC und formatiere ihn so.
      // Das ist die wichtigste Änderung!
      return moment.utc(time).format("HH:mm")
    },
  },
}
</script>

<style scoped>
.booking-shell {
  --booking-primary: #009640;
  --booking-primary-rgb: 0, 150, 64;
  --bg: #0d0f13;
  --surface: #171a20;
  --surface-high: #1d2129;
  --text-primary: #ffffff;
  --text-secondary: #9da4b2;
  --border: rgba(255, 255, 255, 0.09);
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  color: var(--text-primary);
  background: radial-gradient(circle at 88% -6%, rgba(var(--booking-primary-rgb), 0.11), transparent 29rem),
    radial-gradient(circle at -8% 58%, rgba(255, 255, 255, 0.035), transparent 24rem), linear-gradient(160deg, #0b0d11 0%, #11141a 48%, #0d0f13 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.booking-shell::before {
  content: "";
  position: absolute;
  top: -190px;
  left: 50%;
  width: 560px;
  height: 300px;
  border-radius: 50%;
  transform: translateX(-50%);
  background: var(--booking-primary);
  opacity: 0.075;
  filter: blur(105px);
  pointer-events: none;
}

.booking-container {
  position: relative;
  z-index: 1;
  max-width: 980px !important;
  padding: 48px 16px 74px;
}

.booking-loading,
.booking-load-error {
  display: flex;
  min-height: 70vh;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  text-align: center;
}

.booking-loading strong {
  margin-top: 12px;
  color: var(--text-primary);
  font-size: 1rem;
}

.booking-load-error > span {
  display: grid;
  width: 62px;
  height: 62px;
  margin-bottom: 8px;
  place-items: center;
  border-radius: 19px;
  background: rgba(var(--booking-primary-rgb), 0.12);
}

.booking-load-error > span .v-icon {
  color: var(--booking-primary);
  font-size: 31px;
}

.booking-load-error h1 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
}

.booking-load-error p {
  margin: 0 0 16px;
}

.booking-layout {
  max-width: 760px;
  margin: auto;
}

.booking-header {
  max-width: 660px;
  margin: 18px auto 28px;
}

.booking-eyebrow,
.step-kicker,
.dialog-eyebrow {
  display: block;
  color: var(--booking-primary);
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.17em;
}

.booking-offer-icon {
  display: grid;
  width: 58px;
  height: 58px;
  margin: 15px auto 13px;
  place-items: center;
  border: 1px solid rgba(var(--booking-primary-rgb), 0.24);
  border-radius: 18px;
  background: rgba(var(--booking-primary-rgb), 0.11);
}

.booking-offer-icon .v-icon {
  color: var(--booking-primary);
  font-size: 29px;
}

.booking-title {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(2rem, 6vw, 3.1rem);
  font-weight: 900;
  line-height: 1.06;
  letter-spacing: -0.04em;
}

.booking-description {
  max-width: 610px;
  margin: 10px auto 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.55;
}

.booking-progress {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 54px minmax(0, 1fr);
  align-items: center;
  max-width: 600px;
  margin: 0 auto 18px;
}

.progress-step {
  appearance: none;
  display: flex;
  min-width: 0;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid transparent;
  border-radius: 15px;
  color: var(--text-secondary);
  font: inherit;
  text-align: left;
  background: transparent;
  cursor: pointer;
  transition: 160ms ease;
}

.progress-step:disabled {
  cursor: default;
  opacity: 0.58;
}

.progress-step--active,
.progress-step--complete {
  border-color: rgba(var(--booking-primary-rgb), 0.2);
  color: var(--text-primary);
  background: rgba(var(--booking-primary-rgb), 0.08);
}

.progress-step:focus-visible {
  outline: 2px solid var(--booking-primary);
  outline-offset: 2px;
}

.progress-step-number {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 50%;
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 900;
  background: rgba(255, 255, 255, 0.055);
}

.progress-step--active .progress-step-number,
.progress-step--complete .progress-step-number {
  border-color: rgba(var(--booking-primary-rgb), 0.32);
  color: #ffffff;
  background: var(--booking-primary);
}

.progress-step-number .v-icon {
  color: #ffffff;
}

.progress-step-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.progress-step-copy strong {
  font-size: 0.8rem;
  font-weight: 850;
}

.progress-step-copy small {
  overflow: hidden;
  margin-top: 1px;
  color: var(--text-secondary);
  font-size: 0.66rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress-connector {
  height: 2px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.09);
}

.progress-connector--active {
  background: rgba(var(--booking-primary-rgb), 0.48);
}

.booking-panel {
  overflow: hidden;
  border: 1px solid var(--border) !important;
  border-radius: 28px !important;
  color: var(--text-primary) !important;
  background: rgba(20, 23, 29, 0.88) !important;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.32) !important;
  backdrop-filter: blur(18px);
}

.step-wrapper {
  padding: clamp(22px, 4vw, 34px);
}

.step-heading {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 24px;
}

.step-heading--times {
  margin-top: 25px;
}

.step-icon {
  display: grid;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  place-items: center;
  border-radius: 14px;
  background: rgba(var(--booking-primary-rgb), 0.11);
}

.step-icon .v-icon {
  color: var(--booking-primary);
}

.step-heading h2 {
  margin: 3px 0 3px;
  color: var(--text-primary);
  font-size: clamp(1.25rem, 4vw, 1.65rem);
  font-weight: 900;
  letter-spacing: -0.025em;
}

.step-heading p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.date-picker-frame {
  overflow: hidden;
  max-width: 560px;
  margin: auto;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 22px;
  background: rgba(0, 0, 0, 0.14);
}

.date-picker-custom {
  border-radius: 17px !important;
  background: transparent !important;
}

:deep(.date-picker-custom .v-picker__body) {
  background: transparent !important;
}

:deep(.date-picker-custom .v-date-picker-header) {
  padding: 10px 8px 6px;
}

:deep(.date-picker-custom .v-date-picker-header__value button) {
  color: var(--text-primary);
  font-size: 0.92rem;
  font-weight: 800;
  text-transform: none;
}

:deep(.date-picker-custom .v-date-picker-table th) {
  color: var(--text-secondary);
  font-size: 0.68rem;
}

:deep(.date-picker-custom .v-date-picker-table .v-btn) {
  width: 40px;
  height: 40px;
  color: #dce0e7;
  font-size: 0.78rem;
}

:deep(.date-picker-custom .v-date-picker-table .v-btn--active) {
  color: #ffffff !important;
  background: var(--booking-primary) !important;
  box-shadow: 0 5px 16px rgba(var(--booking-primary-rgb), 0.28);
}

.step-footer {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}

.selected-date-preview {
  display: flex;
  min-width: 0;
  gap: 10px;
  align-items: center;
}

.selected-date-preview > .v-icon {
  color: var(--booking-primary);
}

.selected-date-preview div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.selected-date-preview small,
.selected-date-bar small {
  color: var(--text-secondary);
  font-size: 0.66rem;
}

.selected-date-preview strong {
  overflow: hidden;
  color: var(--text-primary);
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.step-primary-action,
.confirm-btn {
  min-height: 48px;
  border-radius: 13px !important;
  font-weight: 800;
}

.selected-date-bar {
  display: flex;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
  padding: 14px 15px;
  border: 1px solid rgba(var(--booking-primary-rgb), 0.18);
  border-radius: 16px;
  background: rgba(var(--booking-primary-rgb), 0.075);
}

.selected-date-bar > span {
  display: flex;
  min-width: 0;
  gap: 10px;
  align-items: center;
}

.selected-date-bar > span > .v-icon {
  color: var(--booking-primary);
}

.selected-date-bar > span > span {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.selected-date-bar strong {
  overflow: hidden;
  color: var(--text-primary);
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.change-date-btn {
  flex: 0 0 auto;
  color: var(--booking-primary) !important;
}

.booking-info-banner,
.booking-limit-info {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  margin-bottom: 14px;
  padding: 12px 13px;
  border: 1px solid rgba(var(--booking-primary-rgb), 0.17);
  border-radius: 14px;
  color: #c9ced7;
  background: rgba(var(--booking-primary-rgb), 0.065);
  font-size: 0.75rem;
  line-height: 1.45;
}

.booking-info-banner .v-icon,
.booking-limit-info .v-icon {
  flex: 0 0 auto;
  color: var(--booking-primary);
}

.booking-limit-info--blocked {
  border-color: rgba(255, 183, 77, 0.24);
  background: rgba(255, 183, 77, 0.08);
}

.booking-limit-info--blocked .v-icon {
  color: #ffb74d;
}

.slots-loading {
  display: flex;
  min-height: 220px;
  flex-direction: column;
  gap: 13px;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 0.8rem;
}

.timeslot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(112px, 1fr));
  gap: 10px;
}

.timeslot-btn {
  height: 62px !important;
  border: 1px solid rgba(var(--booking-primary-rgb), 0.2) !important;
  border-radius: 14px !important;
  color: var(--text-primary) !important;
  background: rgba(var(--booking-primary-rgb), 0.085) !important;
  transition: 160ms ease;
}

:deep(.timeslot-btn .v-btn__content) {
  flex-direction: column;
  gap: 1px;
}

.timeslot-btn span {
  font-size: 1rem;
  font-weight: 850;
}

.timeslot-btn small {
  color: var(--booking-primary);
  font-size: 0.6rem;
  font-weight: 800;
}

.timeslot-btn:disabled {
  border-color: var(--border) !important;
  color: var(--text-secondary) !important;
  background: rgba(255, 255, 255, 0.035) !important;
  opacity: 0.56;
}

.timeslot-btn:disabled small {
  color: var(--text-secondary);
}

.no-slots-container {
  display: flex;
  min-height: 260px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px;
  border: 1px dashed var(--border);
  border-radius: 20px;
  color: var(--text-secondary);
  text-align: center;
  background: rgba(255, 255, 255, 0.018);
}

.no-slots-icon {
  display: grid;
  width: 56px;
  height: 56px;
  margin-bottom: 13px;
  place-items: center;
  border-radius: 17px;
  background: rgba(var(--booking-primary-rgb), 0.1);
}

.no-slots-icon .v-icon {
  color: var(--booking-primary);
  font-size: 28px;
}

.no-slots-container h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
}

.no-slots-container p {
  margin: 6px 0 17px;
  font-size: 0.78rem;
}

.confirm-dialog-card {
  --text-primary: #f8fafc;
  --text-secondary: #b8c0cc;
  --border: rgba(255, 255, 255, 0.11);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 26px !important;
  color: var(--text-primary) !important;
  background: #15181e !important;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.46) !important;
}

.confirm-dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 30px 22px;
  text-align: center;
}

.confirm-icon-wrap {
  display: grid;
  width: 62px;
  height: 62px;
  margin-bottom: 14px;
  place-items: center;
  border: 1px solid rgba(var(--booking-primary-rgb), 0.24);
  border-radius: 19px;
  background: rgba(var(--booking-primary-rgb), 0.11);
}

.confirm-icon-wrap .v-icon {
  color: var(--booking-primary);
  font-size: 31px;
}

.confirm-icon-wrap--info {
  border-color: rgba(255, 183, 77, 0.24);
  background: rgba(255, 183, 77, 0.09);
}

.confirm-icon-wrap--info .v-icon {
  color: #ffb74d;
}

.confirm-icon-wrap--error {
  border-color: rgba(255, 82, 82, 0.24);
  background: rgba(255, 82, 82, 0.09);
}

.confirm-icon-wrap--error .v-icon {
  color: #ff7474;
}

.confirm-title {
  margin: 5px 0 0;
  color: var(--text-primary) !important;
  font-size: 1.5rem;
  font-weight: 900;
  letter-spacing: -0.025em;
}

.confirm-dialog-card .dialog-eyebrow {
  color: var(--booking-primary) !important;
  font-size: 0.75rem;
}

.confirm-text {
  max-width: 380px;
  margin: 7px 0 18px;
  color: var(--text-secondary) !important;
  font-size: 1rem;
  line-height: 1.55;
}

.confirmation-summary {
  display: grid;
  width: 100%;
  gap: 1px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 17px;
  background: var(--border);
  text-align: left;
}

.confirmation-summary > div {
  display: grid;
  grid-template-columns: minmax(100px, 0.75fr) minmax(0, 1.25fr);
  gap: 16px;
  align-items: center;
  padding: 12px 14px;
  background: #191c23;
}

.confirmation-summary span {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  color: var(--text-secondary) !important;
  font-size: 0.875rem;
}

.confirmation-summary span .v-icon {
  color: var(--booking-primary) !important;
}

.confirmation-summary strong {
  min-width: 0;
  color: var(--text-primary) !important;
  font-size: 0.875rem;
  line-height: 1.4;
  text-align: right;
  overflow-wrap: anywhere;
}

.confirm-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 14px 22px 22px !important;
}

.confirm-actions--single {
  justify-content: center;
}

.confirm-actions--single .v-btn {
  min-width: 180px;
}

@media (hover: hover) {
  .progress-step:hover:not(:disabled) {
    border-color: rgba(var(--booking-primary-rgb), 0.18);
    background: rgba(var(--booking-primary-rgb), 0.06);
  }

  .timeslot-btn:hover:not(:disabled) {
    border-color: rgba(var(--booking-primary-rgb), 0.42) !important;
    background: rgba(var(--booking-primary-rgb), 0.18) !important;
    transform: translateY(-2px);
  }
}

@media (max-width: 700px) {
  .booking-container {
    padding: 62px 5px 50px;
  }

  .booking-header {
    margin: 4px auto 23px;
    padding: 0 14px;
  }

  .booking-offer-icon {
    width: 50px;
    height: 50px;
    border-radius: 15px;
  }

  .booking-progress {
    grid-template-columns: minmax(0, 1fr) 24px minmax(0, 1fr);
    margin: 0 8px 14px;
  }

  .progress-step {
    gap: 7px;
    padding: 8px;
  }

  .progress-step-number {
    width: 30px;
    height: 30px;
    flex-basis: 30px;
  }

  .booking-panel {
    border-radius: 22px !important;
  }

  .step-wrapper {
    padding: 21px 17px;
  }

  .step-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .step-primary-action {
    width: 100%;
  }

  .timeslot-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .confirm-actions {
    flex-direction: column-reverse;
  }

  .confirm-actions .v-btn {
    width: 100%;
    margin: 0 !important;
  }
}

@media (max-width: 440px) {
  .booking-title {
    font-size: 1.85rem;
  }

  .progress-step-copy small {
    display: none;
  }

  .progress-step-copy strong {
    font-size: 0.72rem;
  }

  .step-heading {
    gap: 11px;
  }

  .step-icon {
    width: 40px;
    height: 40px;
    flex-basis: 40px;
  }

  .selected-date-bar {
    align-items: flex-start;
  }

  .selected-date-bar strong {
    white-space: normal;
  }

  .change-date-btn {
    min-width: auto !important;
    padding: 0 7px !important;
  }

  .timeslot-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .confirmation-summary > div {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .confirmation-summary strong {
    text-align: left;
  }
}

@media (prefers-reduced-motion: reduce) {
  .progress-step,
  .timeslot-btn {
    transition: none !important;
  }
}
</style>
